/**
 * Public guide: links aligned with the membership funnel (/registro → profile → portal).
 */

import { Button } from "@/components/ui/button";
import {
  MembershipPortalCard,
  MembershipPortalIconBadge,
  MembershipPortalShell,
} from "@/components/membership/MembershipPortalShell";
import { useSEO } from "@/hooks/useSEO";
import { useMembershipFunnelEnglish } from "@/hooks/useMembershipFunnelEnglish";
import { BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_SUPPORT_EMAIL , BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";
import {
  MEMBERSHIP_ACCESS_PORTAL_PATH,
  MEMBERSHIP_ROUTE,
} from "@/lib/membershipAccessFlow";
import { MEMBERSHIP_PORTAL_STEPS } from "@/lib/membershipPortalSteps";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  CreditCard,
  LogIn,
  Sparkles,
  UserPlus,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

const ICON_BY_PATH: Record<string, LucideIcon> = {
  [MEMBERSHIP_ROUTE.REGISTRO]: CreditCard,
  [MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO]: UserPlus,
  [MEMBERSHIP_ROUTE.PENDING_APPROVAL]: Clock,
  [MEMBERSHIP_ROUTE.MI_ACCESO]: CheckCircle2,
  [MEMBERSHIP_ROUTE.ENTRAR]: LogIn,
};

export default function MembershipAccessPortal() {
  useMembershipFunnelEnglish();
  useSEO({
    title: `Membership access steps — ${BRAND_PERSON_NAME}`,
    description: `After payment: create your account, complete your profile, wait for team approval, and get your access link.`,
    keywords: "",
    ogImage: BRAND_OG_IMAGE_URL,
    canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ACCESS_PORTAL_PATH}`,
    robots: "noindex, nofollow" as const,
  });

  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : (BRAND_SITE_URL || "").replace(/\/$/, "");

  return (
    <MembershipPortalShell maxWidthClassName="max-w-2xl">
      <div className="space-y-2 text-center">
        <div className="admin-linear-pill mx-auto gap-1.5 text-amber-200">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          Membership
        </div>
        <h1 className="font-ios-display text-2xl font-semibold tracking-tight text-zinc-50 md:text-[1.75rem]">
          Steps to access your membership
        </h1>
        <p className="mx-auto max-w-md text-sm leading-relaxed text-zinc-400">
          Same order as after payment on {BRAND_PERSON_NAME || "this site"}: pick the option that matches your situation.
        </p>
      </div>

      <ol className="m-0 list-none space-y-3 p-0">
        {MEMBERSHIP_PORTAL_STEPS.map((step, index) => {
          const Icon = ICON_BY_PATH[step.path] ?? UserPlus;
          const highlight = step.highlight === true;
          return (
            <li key={step.path}>
              <MembershipPortalCard
                tone={highlight ? "warning" : "default"}
                className="p-4 sm:p-5"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums ${
                      highlight
                        ? "bg-amber-500/25 text-amber-100 ring-1 ring-amber-500/35"
                        : "bg-white/10 text-zinc-300"
                    }`}
                    aria-hidden
                  >
                    {index + 1}
                  </div>
                  <MembershipPortalIconBadge
                    tone={highlight ? "amber" : "zinc"}
                    className="mx-0 h-10 w-10 rounded-xl"
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </MembershipPortalIconBadge>
                  <div className="min-w-0 flex-1 space-y-1">
                    <h2 className="font-ios-display text-base font-semibold leading-snug tracking-tight text-zinc-50">
                      {step.title}
                    </h2>
                    <p className="text-sm leading-relaxed text-zinc-400">{step.body}</p>
                    <Button
                      asChild
                      className={
                        highlight
                          ? "ios-press mt-3 w-full bg-amber-500 font-semibold text-zinc-950 hover:bg-amber-400 sm:w-auto"
                          : "ios-press mt-3 w-full bg-white font-semibold text-zinc-950 hover:bg-white/90 sm:w-auto"
                      }
                    >
                      <Link
                        to={`${step.path}?lang=en`}
                        className="inline-flex items-center justify-center gap-2"
                      >
                        {step.cta}
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </Link>
                    </Button>
                    <p className="mt-2 break-all font-mono text-[11px] tabular-nums text-zinc-600">
                      {origin}
                      {step.path}
                    </p>
                  </div>
                </div>
              </MembershipPortalCard>
            </li>
          );
        })}
      </ol>

      {BRAND_SUPPORT_EMAIL ? (
        <p className="text-center text-xs text-zinc-500">
          Still stuck? Email us at{" "}
          <a
            href={`mailto:${BRAND_SUPPORT_EMAIL}`}
            className="text-amber-300/90 underline underline-offset-2 hover:text-amber-200"
          >
            {BRAND_SUPPORT_EMAIL}
          </a>{" "}
          with the address you used to pay.
        </p>
      ) : null}

      <p className="text-center">
        <Link
          to="/"
          className="text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-300"
        >
          Back to home
        </Link>
      </p>
    </MembershipPortalShell>
  );
}